import { useCallback, useSyncExternalStore } from 'react'
import type { ProjectionTransportStatus } from '@ovasabi/runtime-transport'
import {
  getActivePrototypeRuntime,
  isLiveRuntimePending,
  subscribePrototypeRuntime,
} from '../stores/activeRuntime'

/**
 * The live read path's health, made observable.
 *
 * Reads are projections: the server sweeps changed rows every ~2s and fans them
 * out over ONE multiplexed WebSocket, so the UI is supposed to update itself
 * with no polling anywhere. The transport already reports what it is doing —
 * connecting / live / reconnecting / degraded / closed, per scope — but nothing
 * in this app ever passed `onProjectionStatus`, so the signal was dropped on the
 * floor. That is what made a dead delta lane indistinguishable from "nothing
 * changed": the socket could give up for good (the transport stops after 8
 * consecutive failed connections) and every screen would keep rendering its
 * last snapshot, silently, until a manual reload.
 *
 * Two things read this:
 *  - the shell, to say so when the live lane is down instead of showing stale
 *    data as if it were current;
 *  - a screen that needs a stronger guarantee than "the delta will come" — it
 *    watches its own scope's revision and re-reads once when the stream drops
 *    or resumes, instead of polling on a timer.
 */
export type ProjectionPhase = ProjectionTransportStatus['phase']

export type ProjectionHealth = {
  /** Coarsest state across every subscribed scope. */
  phase: 'live' | 'connecting' | 'reconnecting' | 'offline'
  /** True once the transport has given up reconnecting (retry budget spent). */
  gaveUp: boolean
}

type ScopeState = { phase: ProjectionPhase; revision: number }

const listeners = new Set<() => void>()
const scopes = new Map<string, ScopeState>()

let health: ProjectionHealth = { phase: 'connecting', gaveUp: false }
let gaveUp = false

const key = (domain: string, collection: string): string => `${domain}/${collection}`

const notify = () => listeners.forEach((listener) => listener())

/**
 * Recompute the coarse health. "Offline" needs every scope to be down, so one
 * scope closing (a screen unmounting its binding) cannot make the whole app
 * claim to be offline.
 */
const recomputeHealth = () => {
  let live = 0
  let reconnecting = 0
  for (const state of scopes.values()) {
    if (state.phase === 'live') live += 1
    else if (state.phase === 'reconnecting' || state.phase === 'degraded') reconnecting += 1
  }
  // "Nothing subscribed" is not an outage: it is the pre-auth app, and it is
  // every scope closing during logout. Only a spent retry budget is offline —
  // otherwise a signed-out visitor would be told live updates are broken.
  const phase: ProjectionHealth['phase'] = gaveUp
    ? 'offline'
    : live > 0
      ? 'live'
      : reconnecting > 0
        ? 'reconnecting'
        : 'connecting'
  if (phase === health.phase && gaveUp === health.gaveUp) return
  health = { phase, gaveUp }
}

/**
 * Feed one transport status in. Wired as `onProjectionStatus` where the live
 * runtime is created, which is the only place the transport reports from.
 */
export const publishProjectionStatus = (status: ProjectionTransportStatus): void => {
  const scopeKey = key(status.scope.domain, status.scope.collection)
  const previous = scopes.get(scopeKey)
  // A scope's revision bumps when its stream breaks and when it comes back, so
  // a consumer that re-reads on the revision covers both the gap and the catch
  // up with exactly two reads — no timer.
  const broke = status.phase === 'degraded' || status.phase === 'reconnecting'
  const resumed = status.phase === 'live' && previous !== undefined && previous.phase !== 'live'
  const revision = (previous?.revision ?? 0) + (broke || resumed ? 1 : 0)
  scopes.set(scopeKey, { phase: status.phase, revision })

  // Only the retry-limit close means "gave up"; an unsubscribe closes a scope
  // as part of normal teardown and must not read as an outage.
  if (status.phase === 'closed' && status.reason === 'retry-limit') gaveUp = true
  if (status.phase === 'live') gaveUp = false

  recomputeHealth()
  notify()
}

const subscribe = (listener: () => void): (() => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

const getHealth = (): ProjectionHealth => health

export const useProjectionHealth = (): ProjectionHealth =>
  useSyncExternalStore(subscribe, getHealth, getHealth)

/**
 * A counter for one scope that changes when its delta stream breaks or resumes.
 * Use it as a `useEffect` dependency to re-read that scope once per transition
 * — the event-driven replacement for a poll.
 */
export const useScopeRevision = (domain: string, collection: string): number => {
  const scopeKey = key(domain, collection)
  return useSyncExternalStore(
    subscribe,
    () => scopes.get(scopeKey)?.revision ?? 0,
    () => 0,
  )
}

const findBinding = (domain: string, collection: string) =>
  getActivePrototypeRuntime().liveBindings.find((binding) => {
    const scope = binding.scope()
    return scope.domain === domain && scope.collection === collection
  })

/**
 * Whether one scope's first snapshot has landed on the active live runtime.
 *
 * Zero records is two different states — "not here yet" and "loaded, and
 * empty" — and a screen that reads only the record count cannot tell them
 * apart, so it shows its loading skeleton forever when a scope is genuinely
 * empty (the production catalogue with its seed rows filtered out, a new
 * tenant, a kitchen with no dishes yet). The binding already knows: its status
 * leaves "idle"/"loading" once the snapshot applies. "error" and "closed" end
 * the wait as well, so a failed scope renders its empty state rather than a
 * skeleton that never resolves. Follows runtime swaps (sign-in, sign-out).
 */
export const useScopeLoaded = (domain: string, collection: string): boolean => {
  const subscribe = useCallback(
    (listener: () => void) => {
      let unbind: () => void = findBinding(domain, collection)?.subscribeStatus(listener) ?? (() => {})
      const unsubscribeRuntime = subscribePrototypeRuntime(() => {
        unbind()
        unbind = findBinding(domain, collection)?.subscribeStatus(listener) ?? (() => {})
        listener()
      })
      return () => {
        unbind()
        unsubscribeRuntime()
      }
    },
    [domain, collection],
  )
  const read = () => {
    const status = findBinding(domain, collection)?.getStatusSnapshot().status
    return status !== undefined && status !== 'idle' && status !== 'loading'
  }
  return useSyncExternalStore(subscribe, read, () => false)
}

/**
 * Reconnect every live binding: re-runs each scope's snapshot + subscribe.
 * The transport's own retry budget is finite by design (a rejected upgrade is
 * indistinguishable from an outage in the browser, so it must not loop), which
 * leaves an explicit retry as the only way back once it is spent.
 */
export const reconnectLiveProjections = (): void => {
  gaveUp = false
  recomputeHealth()
  notify()
  for (const binding of getActivePrototypeRuntime().liveBindings) {
    void binding.connect()
  }
}

/**
 * Whether a list may honestly say it is empty.
 *
 * Live: once the scope's first snapshot has settled (or failed, which the
 * projection banner reports). A scope this session does not stream, and every
 * prototype or offline run, has nothing to wait for and is ready at once; a
 * skeleton that waits on a binding that does not exist would never clear. A
 * signed-in session whose live runtime has not been swapped in yet is NOT an
 * offline run — it waits (see useLiveRuntimePending).
 */
export const useProjectionReady = (domain: string, collection: string): boolean => {
  const loaded = useScopeLoaded(domain, collection)
  const pending = useLiveRuntimePending()
  if (pending) return false
  return loaded || getActivePrototypeRuntime().mode !== 'live' || !findBinding(domain, collection)
}

/**
 * True while a signed-in session is still reading the offline runtime — the
 * frame between sign-in (or a reload) and the live runtime replacing it. Rows
 * read in that window are placeholders, never the user's data.
 */
export const useLiveRuntimePending = (): boolean =>
  useSyncExternalStore(subscribePrototypeRuntime, isLiveRuntimePending, () => false)
